import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Send,
  Check,
  MessageSquare,
  ChevronDown,
  RefreshCw,
  ArrowRight,
  X,
  Edit3,
  Sunrise,
  Sun,
  Sunset,
  Moon,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useTodayBriefing, useRefreshBriefing } from '../hooks/useInsights';
import { useEffectiveRole } from '../hooks/useEffectiveRole';
import { useUserStore } from '../store/userStore';
import { ActionModal } from '../components/actions/ActionModal';
import type { BriefingItem, BriefingVoice, SuggestedAction } from '../types';

// item.id is generated by the LLM (backend/app/workers/insight.py) — an 8
// char alphanumeric string with no entity namespacing and no uniqueness
// guarantee across tenants. It is used as the key into the module-scope
// useActionStore, so two different entities' briefings can (and in
// production do) produce the same id. Composing entityId+role into the
// actionId here makes a collision structurally impossible, independent of
// the identity-change reset in useAuth.ts.
function scopedActionId(itemId: string, entityId: string | undefined, role: string): string {
  return `${entityId ?? 'unknown-entity'}:${role}:${itemId}`;
}

function briefingItemToAction(item: BriefingItem, entityId: string | undefined, role: string): SuggestedAction {
  return {
    actionId: scopedActionId(item.id, entityId, role),
    type: item.actionType,
    label: item.actions.approve,
    description: item.headline,
    roles: item.roles,
    priority: 'high',
    // item.artifact carries the fully-drafted content the AI wrote for
    // voice="acting" items (e.g. the actual doc title/content, not just a
    // generic actionPayload) — it must win over actionPayload where both
    // set the same field, or the specific thing the user asked for (a
    // launch alignment doc, a customer demo plan, ...) never reaches the
    // backend and only the thinner generic payload does.
    payload: { projectId: item.projectId, projectName: item.projectName, ...item.actionPayload, ...(item.artifact ?? {}) },
  };
}

const VOICE_ICON: Record<BriefingVoice, React.ElementType> = {
  acting: Send,
  recommending: Check,
  observational: MessageSquare,
};

function ShimmerBlock({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-gray-200 dark:bg-white/10 ${className ?? ''}`} />;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-10 py-8">
      <div className="space-y-3">
        <ShimmerBlock className="h-5 w-48" />
        <ShimmerBlock className="h-14 w-[70%]" />
        <ShimmerBlock className="h-6 w-[50%]" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="py-7 border-b border-gray-200 dark:border-white/[0.06] flex items-start gap-6">
          <ShimmerBlock className="h-5 w-6 flex-shrink-0 mt-2" />
          <div className="flex-1 space-y-3">
            <ShimmerBlock className="h-3 w-32" />
            <ShimmerBlock className="h-10 w-[80%]" />
          </div>
          <ShimmerBlock className="h-10 w-36 rounded-full flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

/**
 * Single source of truth for "what time is it, for this viewer." The count
 * line's tagline ("this evening") and the page greeting ("Good evening,
 * Chris.") must never disagree — they used to be computed independently
 * (one client-side from local time, one server-side from a briefing
 * document baked at generation time, in UTC) and that's exactly how they
 * drifted. Deriving both from one `getHours()` call here makes that
 * structurally impossible: there is nowhere left for a second clock to
 * creep back in.
 *
 * Boundaries (local time): 0–4 late night, 5–11 morning, 12–16 afternoon,
 * 17–23 evening. Noon and 5pm land on the afternoon/evening side of their
 * boundary (`< 17`, not `<= 17`); midnight and 5am land on the earlier
 * side (`< 5`, `< 12`).
 *
 * The header glyph is returned from here too, for the same reason: an icon
 * that read the clock for itself could show a sunrise beside "this evening"
 * at a boundary. It reads the same `hour` const as the words do, so the two
 * cannot drift apart.
 *
 * Icon buckets are one finer than the text buckets. Evening (17–23) spans
 * both actual sunset and genuine night, and a Sunset glyph at 11pm is
 * wrong about the sky in a way the words are not — "this evening" is still
 * true at 11pm, a setting sun isn't. So the icon (and only the icon) splits
 * at 21:00 to Moon. The greeting and tagline are deliberately unchanged.
 */
function getTimeOfDay(): { tagline: string; greetingPrefix: string; Icon: React.ElementType } {
  const hour = new Date().getHours();

  const Icon =
    hour < 5 ? Moon
    : hour < 12 ? Sunrise
    : hour < 17 ? Sun
    : hour < 21 ? Sunset
    : Moon;

  if (hour < 5) return { tagline: 'tonight', greetingPrefix: 'Good evening', Icon };
  if (hour < 12) return { tagline: 'before coffee', greetingPrefix: 'Good morning', Icon };
  if (hour < 17) return { tagline: 'this afternoon', greetingPrefix: 'Good afternoon', Icon };
  return { tagline: 'this evening', greetingPrefix: 'Good evening', Icon };
}

/** First name for the greeting — always the VIEWING user's, sourced from
 * their own client-side identity (never anything server-cached, which is
 * shared across every colleague viewing this persona's briefing). Mirrors
 * the backend's email-local-part fallback (see _first_name in
 * routers/briefing.py) for the rare case a full name isn't set. */
function viewerFirstName(user: { fullName?: string; email?: string } | null): string {
  const fullName = user?.fullName?.trim();
  if (fullName) return fullName.split(' ')[0];
  const local = user?.email?.split('@')[0] ?? '';
  const localFirst = local.split('.')[0];
  return localFirst ? localFirst.charAt(0).toUpperCase() + localFirst.slice(1) : 'there';
}

/** "45s" / "4m" — coarse on purpose, this is a static snapshot from the
 * refresh response, not a live countdown. */
function formatRetryAfter(seconds: number): string {
  if (seconds <= 0) return 'a moment';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.ceil(seconds / 60)}m`;
}

function EvidenceLink({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => { onClose(); navigate(`/projects/${projectId}`); }}
      className="text-primary font-semibold hover:text-primary/80 focus-visible:ring-2 focus-visible:ring-primary/50 rounded transition-colors"
    >
      See full evidence &rarr;
    </button>
  );
}

// ── Why? popover ─────────────────────────────────────────────────────────────

function WhyPopover({
  title,
  reasons,
  projectId,
  onClose,
}: {
  title: string;
  reasons: string[];
  projectId: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[380px] max-w-[90vw] bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-xl shadow-xl z-20 p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-micro font-semibold text-primary uppercase tracking-wider">
          <Sparkles className="h-4 w-4" />
          {title}
        </div>
        <button
          onClick={onClose}
          aria-label="Close" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors p-1 rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="space-y-2.5">
        {reasons.map((r, i) => (
          <li key={i} className="flex gap-2.5 text-meta text-gray-700 dark:text-gray-300 leading-relaxed">
            <span className="flex-shrink-0 mt-0.5 h-[18px] w-[18px] rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 flex items-center justify-center text-micro font-bold">
              {i + 1}
            </span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-white/[0.06] text-micro text-gray-400 dark:text-gray-500 flex items-center justify-between">
        <span>Pulled from Slack, Jira, Linear &middot; {reasons.length} signals</span>
        <EvidenceLink projectId={projectId} onClose={onClose} />
      </div>
    </div>
  );
}

// ── Split send menu ──────────────────────────────────────────────────────────

function SendMenu({
  cta,
  onClose,
  onAction,
}: {
  cta: string;
  onClose: () => void;
  onAction: (action: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const items = [
    { key: 'edit', label: 'Edit before sending', icon: Edit3, hint: 'Review and tweak before executing' },
  ];

  return (
    <div
      ref={ref}
      className="absolute top-full right-0 mt-1.5 w-[260px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-xl shadow-xl z-20 p-1.5"
    >
      <div className="text-micro font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-2.5 py-2">
        {cta} options
      </div>
      {items.map((it) => (
        <button
          key={it.key}
          onClick={() => { onAction(it.key); onClose(); }}
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset transition-colors text-left"
        >
          <span className="flex-shrink-0 h-7 w-7 rounded-md bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 flex items-center justify-center">
            <it.icon className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-meta font-semibold text-gray-900 dark:text-white leading-tight">{it.label}</span>
            <span className="block text-micro text-gray-400 dark:text-gray-500 leading-tight">{it.hint}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Briefing row (poster style) ──────────────────────────────────────────────

function BriefingRow({
  item,
  num,
  isLast,
  entityId,
  role,
}: {
  item: BriefingItem;
  num: number;
  isLast: boolean;
  entityId: string | undefined;
  role: string;
}) {
  const [whyOpen, setWhyOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalAction, setModalAction] = useState<SuggestedAction | null>(null);

  const ActionIcon = VOICE_ICON[item.voice] ?? MessageSquare;

  const openAction = () => setModalAction(briefingItemToAction(item, entityId, role));

  return (
    <>
      <div
        className={`relative py-7 flex items-start gap-6 ${
          isLast ? '' : 'border-b border-gray-200 dark:border-white/[0.06]'
        }`}
      >
        {/* Number */}
        <span className="flex-shrink-0 pt-2 text-meta font-bold text-gray-300 dark:text-gray-600 tabular-nums tracking-wide w-6">
          0{num}
        </span>

        {/* Center: project kicker + headline */}
        <div className="flex-1 min-w-0">
          <div className="text-micro font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            {item.projectName}
          </div>
          <h2 className="text-headline text-gray-900 dark:text-white">
            {item.headline}
          </h2>
        </div>

        {/* Actions: Why? + split button */}
        <div className="flex-shrink-0 flex items-center gap-5 self-center">
          {/* Why? link + popover */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => { setWhyOpen(!whyOpen); setMenuOpen(false); }}
              className={`text-meta font-medium focus-visible:ring-2 focus-visible:ring-primary/50 rounded transition-colors px-1.5 py-1 ${
                whyOpen
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-400 dark:text-gray-500 underline underline-offset-[3px] decoration-gray-300 dark:decoration-gray-600 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              Why?
            </button>
            <AnimatePresence>
              {whyOpen && item.why?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                >
                  <WhyPopover
                    title={item.voice === 'acting' ? "Why I'm flagging this" : item.voice === 'recommending' ? "Why I'd do this" : 'What I noticed'}
                    reasons={item.why}
                    projectId={item.projectId}
                    onClose={() => setWhyOpen(false)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Split send button */}
          <div className="relative inline-flex items-stretch rounded-full bg-primary shadow-sm shadow-primary/30">
            <button
              onClick={openAction}
              className="inline-flex items-center gap-2 text-meta font-semibold text-white px-3.5 py-2 rounded-l-full hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-inset transition-colors whitespace-nowrap"
            >
              <ActionIcon className="h-[14px] w-[14px]" strokeWidth={2.2} />
              {item.actions.approve}
            </button>
            <span className="w-px bg-white/20 self-stretch my-1.5" />
            <button
              onClick={() => { setMenuOpen(!menuOpen); setWhyOpen(false); }}
              aria-label="More actions" className="inline-flex items-center justify-center text-white px-2.5 rounded-r-full hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-inset transition-colors"
            >
              <ChevronDown
                className={`h-[14px] w-[14px] transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`}
                strokeWidth={2.4}
              />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                >
                  <SendMenu cta={item.actions.approve} onClose={() => setMenuOpen(false)} onAction={() => openAction()} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {modalAction && (
          <ActionModal
            action={modalAction}
            projectId={item.projectId}
            onClose={() => setModalAction(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ── TodayView ────────────────────────────────────────────────────────────────

interface TodayViewProps {
  onBrowseAll: () => void;
}

export function TodayView({ onBrowseAll }: TodayViewProps) {
  const effectiveRole = useEffectiveRole();
  const entityId = useUserStore((s) => s.sessionConfig?.entityId);
  const currentUser = useUserStore((s) => s.currentUser);
  const { data: briefing, isLoading } = useTodayBriefing(effectiveRole);
  // Target the persona this briefing was actually resolved to, not the
  // client's own (possibly stale) guess — see DailyBriefing.role.
  const resolvedRole = briefing?.role ?? effectiveRole;
  const refreshMutation = useRefreshBriefing(resolvedRole);

  // The backend can accept a refresh request and still not regenerate
  // anything — the entity+role cooldown (see routers/briefing.py) returns
  // 200 with cooldown: true rather than erroring. Left unsurfaced, that's
  // indistinguishable from a click that did nothing: the button un-spins
  // and the briefing silently stays the same. This is a static snapshot of
  // the last refresh attempt, not a live countdown — clicking again (once
  // isPending clears) re-checks with the server and refreshes the message
  // with the actual remaining time, or succeeds once the window has passed.
  const cooldown = refreshMutation.data?.cooldown ? refreshMutation.data : undefined;
  const cooldownNotice = cooldown
    ? `Already refreshed recently — try again in ${formatRetryAfter(cooldown.retryAfterSeconds ?? 0)}.`
    : null;

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const items = briefing?.items ?? [];
  // One clock, read once per render, feeds the tagline, the greeting and
  // the header glyph below — see getTimeOfDay().
  const timeOfDay = getTimeOfDay();
  const greeting = `${timeOfDay.greetingPrefix}, ${viewerFirstName(currentUser)}.`;
  const TimeOfDayIcon = timeOfDay.Icon;

  return (
    <div className="mx-auto px-6 sm:px-10 lg:px-16 py-6">
      {isLoading && <LoadingSkeleton />}

      {!isLoading && !briefing && (
        <div className="py-24 text-center space-y-4">
          <Sparkles className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto" />
          <p className="text-body font-semibold text-gray-600 dark:text-gray-400">
            No briefing generated yet.
          </p>
          <p className="text-meta text-gray-400 dark:text-gray-500">
            Briefings are generated each morning based on your active projects.
          </p>
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-meta font-semibold rounded-full hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 transition-colors disabled:opacity-50 shadow-sm shadow-primary/30"
          >
            <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            Generate now
          </button>
          {cooldownNotice && (
            <p className="text-micro text-gray-400 dark:text-gray-500">{cooldownNotice}</p>
          )}
        </div>
      )}

      {!isLoading && briefing && (
        <>
          {/* Header bar */}
          <div className="flex items-center justify-between pb-5 border-b border-gray-200 dark:border-white/[0.06]">
            <div className="flex items-center gap-2.5">
              <TimeOfDayIcon className="h-5 w-5 text-primary" />
              <span className="text-meta font-bold text-gray-900 dark:text-white tracking-tight">
                Today
              </span>
              <span className="text-meta text-gray-400 dark:text-gray-500">&middot;</span>
              <span className="text-meta text-gray-400 dark:text-gray-500">{today}</span>
            </div>
            <div className="flex items-center gap-2.5">
              {cooldownNotice && (
                <span className="text-micro text-gray-400 dark:text-gray-500">{cooldownNotice}</span>
              )}
              <button
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
                className="flex items-center gap-1.5 text-micro text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus-visible:ring-2 focus-visible:ring-primary/50 rounded transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Count line */}
          <div className="pt-8 pb-2">
            <div className="text-meta font-semibold text-primary uppercase tracking-widest mb-6">
              {items.length} {items.length === 1 ? 'thing' : 'things'} &middot; {timeOfDay.tagline}
            </div>

            {/* Display headline */}
            <h1 className="text-display text-gray-900 dark:text-white">
              {greeting}
            </h1>
            <p className="mt-4 text-lede text-gray-500 dark:text-gray-400 max-w-xl">
              {briefing.headline}
            </p>
          </div>

          {/* Divider */}
          <div className="w-12 h-0.5 bg-gray-900 dark:bg-white rounded-full my-8" />

          {/* Briefing rows */}
          {items.map((item, i) => (
            <BriefingRow
              key={item.id}
              item={item}
              num={i + 1}
              isLast={i === items.length - 1}
              entityId={entityId}
              role={resolvedRole}
            />
          ))}

          {/* Resting footer */}
          <div className="mt-8 pt-5 border-t border-gray-200 dark:border-white/[0.06] flex items-center justify-between text-meta text-gray-500 dark:text-gray-400">
            <span>
              <strong className="text-gray-900 dark:text-white font-bold">
                {briefing.resting.onTrack}
              </strong>{' '}
              healthy &middot;{' '}
              <strong className="text-gray-900 dark:text-white font-bold">
                {briefing.resting.complete}
              </strong>{' '}
              shipped
            </span>
            <button
              onClick={onBrowseAll}
              className="inline-flex items-center gap-1 font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus-visible:ring-2 focus-visible:ring-primary/50 rounded transition-colors"
            >
              See all {briefing.resting.total} projects
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Stale */}
          {briefing.isStale && (
            <p className="mt-6 text-micro text-center text-gray-400 dark:text-gray-500">
              Briefing may be outdated.{' '}
              <button
                onClick={() => refreshMutation.mutate()}
                className="underline hover:text-gray-600 dark:hover:text-gray-300 focus-visible:ring-2 focus-visible:ring-primary/50 rounded transition-colors"
              >
                Refresh
              </button>
            </p>
          )}
        </>
      )}
    </div>
  );
}
